import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

// ── Tipos ────────────────────────────────────────────────────────────────

export interface Oportunidade {
  id: string;
  titulo: string;
  tipo: string; // prefeitura, estado, rede_privada, parceiro
  etapaFunil: string; // prospeccao, contato, apresentacao, proposta, negociacao, ganho, perdido
  responsavelId: string;
  municipioId?: string | null;
  estadoId?: string | null;
  contato?: string | null;
  telefone?: string | null;
  email?: string | null;
  valorEstimado?: number | null;
  dataPrevisao?: string | null;
  observacoes?: string | null;
  criadoEm: string;
  atualizadoEm: string;
  ativo: boolean;
  responsavel: { id: string; nome: string; email: string; fotoUrl?: string | null };
  municipio?: { id: string; nome: string } | null;
  estado?: { id: string; nome: string; sigla: string } | null;
  interacoes?: Interacao[];
  _count?: { interacoes: number };
}

export interface Interacao {
  id: string;
  oportunidadeId: string;
  tipo: string; // ligacao, email, reuniao, visita, apresentacao, proposta
  descricao: string;
  data: string;
  responsavelId: string;
  criadoEm: string;
  responsavel: { id: string; nome: string; fotoUrl?: string | null };
}

export interface Contrato {
  id: string;
  numero: string;
  titulo: string;
  tipo: string; // municipal, estadual, privado
  municipioId?: string | null;
  estadoId?: string | null;
  redeId?: string | null;
  valorTotal: number;
  dataInicio: string;
  dataFim: string;
  status: string; // ativo, suspenso, encerrado, rescindido
  observacoes?: string | null;
  criadoPorId: string;
  criadoEm: string;
  atualizadoEm: string;
  ativo: boolean;
  criadoPor: { id: string; nome: string; email: string };
  municipio?: { id: string; nome: string } | null;
  estado?: { id: string; nome: string; sigla: string } | null;
  rede?: { id: string; nome: string } | null;
}

// DTOs
export interface CreateOportunidadeInput {
  titulo: string;
  tipo: string;
  etapaFunil?: string;
  responsavelId: string;
  municipioId?: string;
  estadoId?: string;
  contato?: string;
  telefone?: string;
  email?: string;
  valorEstimado?: number;
  dataPrevisao?: string;
  observacoes?: string;
}

export interface CreateContratoInput {
  numero: string;
  titulo: string;
  tipo: string;
  municipioId?: string;
  estadoId?: string;
  redeId?: string;
  valorTotal: number;
  dataInicio: string;
  dataFim: string;
  status?: string;
  observacoes?: string;
}

export interface CreateInteracaoInput {
  tipo: string;
  descricao: string;
  data: string;
}

// ── Hooks ────────────────────────────────────────────────────────────────

const BASE = '/comercial';
const QK_OPORTUNIDADES = 'comercial_oportunidades';
const QK_FUNIL = 'comercial_funil';
const QK_CONTRATOS = 'comercial_contratos';
const QK_CONFIG = 'comercial_config';
const QK_CONTAGEM = 'comercial_contagem';

// ── Tipo de Etapa Configurável ──

export interface EtapaFunil {
  id: string;
  label: string;
  color: string;
  ordem: number;
  visivel: boolean;
}

// ── Hook: Etapas configuráveis ──

export function useEtapasConfig() {
  return useQuery({
    queryKey: [QK_CONFIG, 'etapas_funil'],
    queryFn: async () => {
      const { data } = await api.get<{ chave: string; items: EtapaFunil[] }>(`${BASE}/config/etapas_funil`);
      return data.items || [];
    },
    staleTime: 5 * 60 * 1000, // 5 min cache
  });
}

export function useContagemEtapas() {
  return useQuery({
    queryKey: [QK_CONTAGEM],
    queryFn: async () => {
      const { data } = await api.get<Record<string, number>>(`${BASE}/etapas/contagem`);
      return data;
    },
  });
}

export function useSalvarConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: EtapaFunil[]) =>
      api.put(`${BASE}/config/etapas_funil`, { items }).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QK_CONFIG] });
      queryClient.invalidateQueries({ queryKey: [QK_CONTAGEM] });
    },
  });
}

// ── Hooks Oportunidades ──

export function useOportunidades(filtros?: {
  etapaFunil?: string;
  responsavelId?: string;
  municipioId?: string;
  estadoId?: string;
  busca?: string;
  orderBy?: string;
  orderDir?: 'asc' | 'desc';
  page?: number;
  limite?: number;
}) {
  return useQuery({
    queryKey: [QK_OPORTUNIDADES, filtros],
    queryFn: async () => {
      const { data } = await api.get<{
        items: Oportunidade[];
        total: number;
        page: number;
        limite: number;
        totalPages: number;
      }>(`${BASE}/oportunidades`, { params: filtros });
      return data;
    },
  });
}

export function useOportunidadeDetalhe(id: string | undefined) {
  return useQuery({
    queryKey: [QK_OPORTUNIDADES, 'detalhe', id],
    queryFn: async () => {
      if (!id) return null;
      const { data } = await api.get<Oportunidade>(`${BASE}/oportunidades/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useFunil() {
  return useQuery({
    queryKey: [QK_FUNIL],
    queryFn: async () => {
      const { data } = await api.get<Oportunidade[]>(`${BASE}/funil`);
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useComercialMutations() {
  const queryClient = useQueryClient();

  const criarOportunidade = useMutation({
    mutationFn: (input: CreateOportunidadeInput) =>
      api.post<Oportunidade>(`${BASE}/oportunidades`, input).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QK_OPORTUNIDADES] });
      queryClient.invalidateQueries({ queryKey: [QK_FUNIL] });
    },
  });

  const atualizarOportunidade = useMutation({
    mutationFn: ({ id, ...input }: Partial<CreateOportunidadeInput> & { id: string; ativo?: boolean }) =>
      api.patch<Oportunidade>(`${BASE}/oportunidades/${id}`, input).then(r => r.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QK_OPORTUNIDADES] });
      queryClient.invalidateQueries({ queryKey: [QK_FUNIL] });
      queryClient.invalidateQueries({ queryKey: [QK_OPORTUNIDADES, 'detalhe', variables.id] });
    },
  });

  const atualizarEtapaOportunidade = useMutation({
    mutationFn: ({ id, etapaFunil }: { id: string; etapaFunil: string }) =>
      api.patch<Oportunidade>(`${BASE}/oportunidades/${id}/etapa`, { etapaFunil }).then(r => r.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QK_OPORTUNIDADES] });
      queryClient.invalidateQueries({ queryKey: [QK_FUNIL] });
      queryClient.invalidateQueries({ queryKey: [QK_OPORTUNIDADES, 'detalhe', variables.id] });
    },
  });

  const excluirOportunidade = useMutation({
    mutationFn: (id: string) =>
      api.delete(`${BASE}/oportunidades/${id}`).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QK_OPORTUNIDADES] });
      queryClient.invalidateQueries({ queryKey: [QK_FUNIL] });
    },
  });

  const criarInteracao = useMutation({
    mutationFn: ({ oportunidadeId, ...input }: CreateInteracaoInput & { oportunidadeId: string }) =>
      api.post<Interacao>(`${BASE}/oportunidades/${oportunidadeId}/interacoes`, input).then(r => r.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QK_OPORTUNIDADES, 'detalhe', variables.oportunidadeId] });
    },
  });

  // ── Contratos Mutations ──

  const criarContrato = useMutation({
    mutationFn: (input: CreateContratoInput) =>
      api.post<Contrato>(`${BASE}/contratos`, input).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QK_CONTRATOS] });
    },
  });

  const atualizarContrato = useMutation({
    mutationFn: ({ id, ...input }: Partial<CreateContratoInput> & { id: string; ativo?: boolean }) =>
      api.put<Contrato>(`${BASE}/contratos/${id}`, input).then(r => r.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QK_CONTRATOS] });
      queryClient.invalidateQueries({ queryKey: [QK_CONTRATOS, 'detalhe', variables.id] });
    },
  });

  const excluirContrato = useMutation({
    mutationFn: (id: string) =>
      api.delete(`${BASE}/contratos/${id}`).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QK_CONTRATOS] });
    },
  });

  return {
    criarOportunidade,
    atualizarOportunidade,
    atualizarEtapaOportunidade,
    excluirOportunidade,
    criarInteracao,
    criarContrato,
    atualizarContrato,
    excluirContrato,
  };
}

// ── Hooks Contratos ──

export function useContratos(filtros?: {
  tipo?: string;
  status?: string;
  municipioId?: string;
  estadoId?: string;
  redeId?: string;
  busca?: string;
  page?: number;
  limite?: number;
}) {
  return useQuery({
    queryKey: [QK_CONTRATOS, filtros],
    queryFn: async () => {
      const { data } = await api.get<{
        items: Contrato[];
        total: number;
        page: number;
        limite: number;
        totalPages: number;
      }>(`${BASE}/contratos`, { params: filtros });
      return data;
    },
  });
}

export function useContratoDetalhe(id: string | undefined) {
  return useQuery({
    queryKey: [QK_CONTRATOS, 'detalhe', id],
    queryFn: async () => {
      if (!id) return null;
      const { data } = await api.get<Contrato>(`${BASE}/contratos/${id}`);
      return data;
    },
    enabled: !!id,
  });
}
